import { memo, useMemo } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckboxItem } from "../CheckboxItem";
import type { QuestionnaireItem } from "@/types/maturity";

type PrelimCategoryGroupProps = {
  domainCode: string;
  domainName: string;
  domainColor: string;
  domainBg: string;
  items: QuestionnaireItem[];
  responses: Record<string, boolean>;
  onCheckboxChange: (itemIndex: string, checked: boolean) => void;
  onToggleAll: (itemIndexes: string[], checked: boolean) => void;
};

function PrelimCategoryGroupInner({
  domainCode,
  domainName,
  domainColor,
  domainBg,
  items,
  responses,
  onCheckboxChange,
  onToggleAll,
}: PrelimCategoryGroupProps) {
  const { checkedCount, totalCount, itemIndexes } = useMemo(() => {
    const indexes = items.map((item) => String(item.item_index));
    const checked = indexes.filter((idx) => responses[idx] === true).length;
    return {
      checkedCount: checked,
      totalCount: indexes.length,
      itemIndexes: indexes,
    };
  }, [items, responses]);

  const triState =
    checkedCount === 0
      ? false
      : checkedCount === totalCount
        ? true
        : "indeterminate";

  const handleHeaderClick = () => {
    const shouldCheck = checkedCount < totalCount;
    onToggleAll(itemIndexes, shouldCheck);
  };

  return (
    <div
      className="rounded-lg border overflow-hidden"
      style={{ borderColor: `${domainColor}30` }}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={handleHeaderClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleHeaderClick();
          }
        }}
        className="w-full flex items-center gap-3 px-4 py-2.5 transition-colors hover:opacity-90 cursor-pointer"
        style={{ backgroundColor: domainBg }}
      >
        <Checkbox
          checked={triState}
          onCheckedChange={() => handleHeaderClick()}
          className="pointer-events-none"
          style={{
            borderColor: domainColor,
            backgroundColor: triState === true ? domainColor : undefined,
          }}
        />
        <span
          className="text-xs font-semibold tracking-wide uppercase"
          style={{ color: domainColor }}
        >
          {domainCode} — {domainName}
        </span>
        <span className="ml-auto text-xs text-slate-400">
          {checkedCount}/{totalCount}
        </span>
      </div>

      <div className="divide-y divide-slate-100">
        {items.map((item) => (
          <CheckboxItem
            key={item.item_index}
            item={item}
            checked={responses[String(item.item_index)] === true}
            onChange={(checked) =>
              onCheckboxChange(String(item.item_index), checked)
            }
            domainColor={domainColor}
          />
        ))}
      </div>
    </div>
  );
}

const PrelimCategoryGroup = memo(PrelimCategoryGroupInner);
export default PrelimCategoryGroup;
